import { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { usePreorderCheckout } from '@pos/shared';
import {
  getApiBase,
  getApiHeaders,
  getTenantIdFromUrl,
} from '../../api/apiUtils';
import { remoteLogger } from '../../utils/logger';
import { useTenant } from '../../contexts/TenantContext';
import { TenantStorage } from '../../utils/tenantStorage';

interface PaymentStatus {
  status: 'loading' | 'success' | 'error';
  orderNumber?: string;
  message?: string;
  /** 超商貨到付款成立（訂單已成立，取貨時付款） */
  isCodSuccess?: boolean;
  /** 面交付款模式（訂單已成立，取貨時付款，非線上支付失敗） */
  isPickupPaySuccess?: boolean;
}

/**
 * 從 URL 查詢參數中獲取值
 */
function getQueryParam(name: string): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get(name);
}

/**
 * 取得用於確認付款的 orderId（支援後端 302 帶的 orderId，或綠界可能帶的 MerchantTradeNo、CustomField1）
 */
function getOrderIdFromQuery(): string | null {
  return (
    getQueryParam('orderId') ||
    getQueryParam('MerchantTradeNo') ||
    getQueryParam('CustomField1') ||
    null
  );
}

/**
 * 取得 transactionId（LINE Pay 回調會帶）
 */
function getTransactionIdFromQuery(): string | null {
  return getQueryParam('transactionId') || null;
}

/**
 * 取得 orderNumber（前端導向 payment/return?orderNumber=xxx 時會帶）
 */
function getOrderNumberFromQuery(): string | null {
  return getQueryParam('orderNumber') || null;
}

/** 同一筆交易的 confirm 結果（供跨 instance 共用） */
type ConfirmResult = {
  status: string;
  orderNumber?: string;
  message?: string;
  tenantId?: string;
  isCodSuccess?: boolean;
  isPickupPaySuccess?: boolean;
};

/** 支付確認重試次數（暫時性失敗時不立刻讓客人看到錯誤） */
const PAYMENT_CONFIRM_MAX_RETRIES = 3;
/** 重試間隔（毫秒），第 n 次重試前等待 n * RETRY_DELAY_MS */
const PAYMENT_CONFIRM_RETRY_DELAY_MS = 2000;

const delay = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

/**
 * 模組級：同一筆交易只發送一次 confirm，避免 React Strict Mode 雙重 mount 或雙 tab 重複呼叫。
 * Key = transactionId 時用 `t_${id}`，僅 orderId 時用 `o_${id}`。
 */
const paymentConfirmPromiseByKey = new Map<string, Promise<ConfirmResult>>();

/**
 * 模組級：同一筆交易只顯示一次成功/失敗 toast，避免雙 instance 各顯示一次。
 */
const paymentToastShownKeys = new Set<string>();

/**
 * 模組級：同一錯誤情境只上報一次遠端 log，避免 Strict Mode / 依賴變動導致 effect 重跑時重複送多筆。
 */
const paymentErrorLoggedKeys = new Set<string>();

/**
 * LINE Pay / 綠界 支付完成回調頁面
 * 使用模組級 promise 與 toast 標記，確保 Strict Mode 雙重 mount 時只呼叫一次 confirm、只顯示一次 toast；
 * 確認失敗時會重試數次，僅在重試用盡後才顯示錯誤給客人，避免暫時性失敗造成誤報。
 */
export function PaymentReturnPage() {
  const navigate = useNavigate();
  const { confirmPaymentFromQuery } = usePreorderCheckout();
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>({
    status: 'loading',
  });
  const apiBase = getApiBase();
  const { storage, tenantId: contextTenantId } = useTenant();

  const tenantIdRef = useRef<string | null>(null);

  // 清除購物車的函數（包成 useCallback 以穩定 useEffect 依賴）
  const clearCart = useCallback(() => {
    try {
      // 優先使用 context 的 storage，如果沒有則從 URL 取得 tenantId 並建立 TenantStorage
      if (storage) {
        console.log('[PaymentReturnPage] 清除購物車（使用 context storage）');
        storage.removeItem('cart');
      } else {
        const tenantId = contextTenantId || getTenantIdFromUrl();
        if (tenantId) {
          const tenantStorage = new TenantStorage(tenantId);
          console.log(
            '[PaymentReturnPage] 清除購物車（使用 URL tenantId）',
            tenantId
          );
          tenantStorage.removeItem('cart');
        }
      }
    } catch (error) {
      console.error('[PaymentReturnPage] 清除購物車失敗:', error);
    }
  }, [storage, contextTenantId]);

  // 初始化時，先嘗試從 URL 獲取 tenantId 並設置到 logger（如果有的話）
  // 這樣可以確保後續日誌都有 tenantId，即使 URL 中有 tenantId 參數
  useEffect(() => {
    const urlTenantId = getQueryParam('tenantId') || getQueryParam('tenant_id');
    if (urlTenantId) {
      remoteLogger.setTenantId(urlTenantId);
      console.log('[PaymentReturnPage] 從 URL 獲取 tenantId 並設置到 logger', {
        urlTenantId,
      });
    }
  }, []);

  useEffect(() => {
    const transactionId = getTransactionIdFromQuery();
    const orderIdFromQuery = getOrderIdFromQuery();
    const orderNumberFromUrl = getOrderNumberFromQuery();

    const run = async () => {
      tenantIdRef.current = contextTenantId || getTenantIdFromUrl() || null;
      let orderId = orderIdFromQuery;

      const applySuccess = (res: ConfirmResult) => {
        tenantIdRef.current =
          res.tenantId ||
          tenantIdRef.current ||
          contextTenantId ||
          getTenantIdFromUrl() ||
          null;
        setPaymentStatus({
          status: 'success',
          orderNumber: res.orderNumber,
          isCodSuccess: res.isCodSuccess === true,
          isPickupPaySuccess: res.isPickupPaySuccess === true,
        });
        clearCart();
      };

      const isPickupPayReturn =
        getQueryParam('pickupPay') === '1' ||
        getQueryParam('pickupPay') === 'true';
      if (isPickupPayReturn && orderNumberFromUrl) {
        tenantIdRef.current =
          contextTenantId || getTenantIdFromUrl() || tenantIdRef.current;
        applySuccess({
          status: 'success',
          orderNumber: orderNumberFromUrl,
          isPickupPaySuccess: true,
        });
        return;
      }

      const showSuccessToastOnce = (key: string, isCod: boolean) => {
        if (paymentToastShownKeys.has(key)) return;
        paymentToastShownKeys.add(key);
        toast.success(
          isCod
            ? '訂單已成立，請至超商取貨時付款'
            : '預購成功，系統已送出網站通知'
        );
      };

      const showErrorToastOnce = (key: string, msg: string) => {
        if (paymentToastShownKeys.has(key)) return;
        paymentToastShownKeys.add(key);
        toast.error(msg);
      };

      console.log('[PaymentReturnPage] 載入時 URL 與參數', {
        href: window.location.href,
        search: window.location.search,
        transactionId: transactionId ?? null,
        orderId: orderId ?? null,
        orderNumber: orderNumberFromUrl ?? null,
      });

      try {
        // 僅有 orderNumber（前端導向 payment/return?orderNumber=xxx）：用 by-order-number 解析
        if (
          !transactionId &&
          !orderId &&
          orderNumberFromUrl &&
          contextTenantId
        ) {
          const res = await fetch(
            `${apiBase}/api/preorders/payment/info/by-order-number?orderNumber=${encodeURIComponent(orderNumberFromUrl.trim())}`,
            { headers: getApiHeaders(contextTenantId) }
          );
          if (res.ok) {
            const json = (await res.json()) as {
              success?: boolean;
              data?: {
                orderId: string;
                orderNumber: string;
                paymentStatus: string;
                payment_method?: string;
              };
            };
            if (json?.success && json?.data) {
              orderId = json.data.orderId;
              const paid = json.data.paymentStatus === '已付款';
              const isCod =
                json.data.payment_method === 'cvs_cod' &&
                json.data.paymentStatus !== '已付款';
              const isPickupPay =
                json.data.payment_method === 'pickup_pay' &&
                json.data.paymentStatus !== '已付款';
              if (paid || isCod || isPickupPay) {
                tenantIdRef.current =
                  tenantIdRef.current ||
                  contextTenantId ||
                  getTenantIdFromUrl() ||
                  null;
                applySuccess({
                  status: 'success',
                  orderNumber: json.data.orderNumber,
                  isCodSuccess: isCod,
                  isPickupPaySuccess: isPickupPay,
                });
                const key = `n_${orderNumberFromUrl}`;
                if (isCod) {
                  showSuccessToastOnce(key, true);
                } else if (!isPickupPay) {
                  showSuccessToastOnce(key, false);
                }
                return;
              }
            }
          }
        }

        if (!transactionId && !orderId) {
          throw new Error('missing transactionId and orderId');
        }

        const key = transactionId
          ? `t_${transactionId}`
          : `o_${String(orderId)}`;

        for (
          let attempt = 0;
          attempt < PAYMENT_CONFIRM_MAX_RETRIES;
          attempt++
        ) {
          if (attempt > 0) {
            paymentConfirmPromiseByKey.delete(key);
            await delay(PAYMENT_CONFIRM_RETRY_DELAY_MS * attempt);
          }

          try {
            let promise = paymentConfirmPromiseByKey.get(key);
            if (!promise) {
              promise = (async (): Promise<ConfirmResult> => {
                const result = await confirmPaymentFromQuery({
                  apiBase,
                  getApiHeaders,
                  transactionId,
                  orderId,
                  contextTenantId,
                });
                return {
                  status: result.status,
                  orderNumber: result.orderNumber,
                  message: result.message,
                  tenantId: (result as { tenantId?: string }).tenantId,
                  isCodSuccess: (result as { isCodSuccess?: boolean })
                    .isCodSuccess,
                  isPickupPaySuccess: (
                    result as { isPickupPaySuccess?: boolean }
                  ).isPickupPaySuccess,
                };
              })();
              paymentConfirmPromiseByKey.set(key, promise);
            }

            const result = await promise;

            if (result.status === 'success') {
              applySuccess(result);
              if (!result.isPickupPaySuccess) {
                showSuccessToastOnce(key, result.isCodSuccess === true);
              }
              return;
            }

            setPaymentStatus({
              status: 'error',
              message: result.message || '付款尚未完成',
            });
            return;
          } catch (err) {
            const errorMsg = err instanceof Error ? err.message : String(err);
            remoteLogger.log(
              'error',
              '[PaymentReturnPage] 支付確認錯誤（重試中）',
              {
                attempt: attempt + 1,
                maxRetries: PAYMENT_CONFIRM_MAX_RETRIES,
                error: errorMsg,
                transactionId: transactionId ?? undefined,
                orderId: orderId ?? undefined,
              }
            );

            if (attempt === PAYMENT_CONFIRM_MAX_RETRIES - 1) {
              const rawMsg =
                err instanceof Error ? err.message : '支付確認失敗';
              const msg =
                rawMsg === 'missing transactionId and orderId'
                  ? '無法取得付款資訊，請確認是否已完成付款，或從預購頁面重新操作。'
                  : rawMsg;
              await remoteLogger.flush();
              setPaymentStatus({ status: 'error', message: msg });
              showErrorToastOnce(key, msg);
            }
          }
        }
      } catch (err) {
        const rawMsg = err instanceof Error ? err.message : '支付確認失敗';
        const msg =
          rawMsg === 'missing transactionId and orderId'
            ? '無法取得付款資訊，請確認是否已完成付款，或從預購頁面重新操作。'
            : rawMsg;
        const key = transactionId
          ? `t_${transactionId}`
          : orderIdFromQuery
            ? `o_${orderIdFromQuery}`
            : 'e_missing';
        // 同一錯誤 key 只上報一次遠端 log，避免 effect 重跑（Strict Mode / 依賴變動）時一秒內送多筆
        if (!paymentErrorLoggedKeys.has(key)) {
          paymentErrorLoggedKeys.add(key);
          remoteLogger.log('error', '[PaymentReturnPage] 支付確認錯誤', {
            error: rawMsg,
            stack: err instanceof Error ? err.stack : undefined,
          });
          await remoteLogger.flush();
        } else {
          const orig = remoteLogger.getOriginalConsoleError();
          if (orig) orig.call(console, '[PaymentReturnPage] 支付確認錯誤', err);
        }
        setPaymentStatus({ status: 'error', message: msg });
        showErrorToastOnce(key, msg);
      }
    };

    run();
  }, [apiBase, clearCart, contextTenantId, confirmPaymentFromQuery]);

  if (paymentStatus.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 text-center space-y-4 max-w-md w-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="text-gray-600">正在確認支付...</p>
          <p className="text-xs text-gray-500">請稍候，這可能需要幾秒鐘</p>
        </div>
      </div>
    );
  }

  if (paymentStatus.status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 text-center space-y-4 max-w-md w-full">
          <div className="text-red-600 text-5xl">✗</div>
          <h2 className="text-xl font-bold text-gray-900">支付失敗</h2>
          <p className="text-sm text-gray-600">
            {paymentStatus.message || '支付過程中發生錯誤'}
          </p>
          <button
            type="button"
            onClick={() => {
              // 使用從 D1 獲取的 tenantId（存儲在 ref 中）
              const finalTenantId = tenantIdRef.current;
              const preorderUrl = finalTenantId
                ? `/preorder?tenantId=${encodeURIComponent(finalTenantId)}`
                : '/preorder';
              console.log('[PaymentReturnPage] 返回預購頁面', {
                finalTenantId,
                preorderUrl,
                note: finalTenantId
                  ? '✅ 使用從 D1 獲取的 tenantId 導航'
                  : '⚠️ 沒有 tenantId，可能使用 default',
              });
              navigate(preorderUrl);
            }}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
          >
            返回預購頁面
          </button>
        </div>
      </div>
    );
  }

  const isCodSuccess = paymentStatus.isCodSuccess === true;
  const isPickupPaySuccess = paymentStatus.isPickupPaySuccess === true;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="bg-white rounded-2xl shadow-lg p-6 text-center space-y-4 max-w-md w-full">
        <div className="text-green-600 text-5xl">✓</div>
        <h2 className="text-xl font-bold text-gray-900">
          {isPickupPaySuccess || isCodSuccess ? '訂單已成立' : '預購成功'}
        </h2>
        <p className="text-sm text-gray-600">
          {isPickupPaySuccess
            ? '請於取貨日前往門市付款取貨，並保留以下訂單編號。'
            : isCodSuccess
              ? '請至超商取貨時付款，並保留以下訂單編號以利取貨。'
              : '系統已同步網站通知，請保留以下資訊以利取貨。'}
        </p>
        {paymentStatus.orderNumber && (
          <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
            <p>
              訂單編號：
              <span className="font-semibold">{paymentStatus.orderNumber}</span>
            </p>
          </div>
        )}
        <button
          type="button"
          onClick={() => {
            const finalTenantId = tenantIdRef.current;
            const params = new URLSearchParams();
            if (finalTenantId) {
              params.set('tenantId', finalTenantId);
            }
            const memberPath = `/member${params.toString() ? `?${params.toString()}` : ''}`;
            const memberUrl = `${window.location.origin}${memberPath}`;

            console.log('[PaymentReturnPage] 回會員中心（整頁導向）', {
              finalTenantId,
              memberUrl,
            });
            window.location.assign(memberUrl);
          }}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
        >
          回會員中心
        </button>
      </div>
    </div>
  );
}
